import type { SpartanProductCustomizeStyleName } from '@/app/lcv/components/sections/spartan/products/customize/types';

/** Pickup / X-Series studio keys, plus MU-X semantic keys. */
export type SpartanCustomizePreviewTone =
  | 'light'
  | 'light-dark'
  | 'opaque'
  | 'cool'
  | 'black'
  | 'silver'
  | 'white';

/** Pickup gray studio. */
const LIGHT_DARK_COLOR_IDS = new Set([
  'bavarian-black-mica',
  'elbrus-gray-opaque',
]);

const OPAQUE_COLOR_IDS = new Set(['inishmore-gray-opaque']);

/** X-Series cyan accent — white paints. */
const X_SERIES_CYAN_COLOR_IDS = new Set([
  'siberian-white',
  'dolomite-pearl-white',
  'dolomite-white-pearl',
]);

/** MU-X / Ultimate — cool blue default; black / silver / white studios. */
const MU_X_STYLE_NAMES = new Set<SpartanProductCustomizeStyleName>([
  'mu-x',
  'mu-x-ultimate',
]);
const MU_X_BLACK_COLOR_IDS = new Set(['bavarian-black-mica']);
const MU_X_SILVER_COLOR_IDS = new Set([
  'iceberg-silver-mica',
  'bohemian-silver-metallic',
]);
const MU_X_WHITE_COLOR_IDS = new Set([
  'dolomite-pearl-white',
  'dolomite-white-pearl',
]);

const normalizeColorId = (colorId?: string) =>
  (colorId ?? '').trim().toLowerCase().replace(/\s+/g, '-');

export const getSpartanCustomizePreviewTone = (
  colorId?: string,
  styleName?: SpartanProductCustomizeStyleName
): SpartanCustomizePreviewTone => {
  const normalizedColorId = normalizeColorId(colorId);

  if (styleName === 'x-series') {
    // White → cyan (`light`); black and other paints → red (`light-dark`).
    return X_SERIES_CYAN_COLOR_IDS.has(normalizedColorId)
      ? 'light'
      : 'light-dark';
  }

  if (styleName && MU_X_STYLE_NAMES.has(styleName)) {
    if (MU_X_BLACK_COLOR_IDS.has(normalizedColorId)) return 'black';
    if (MU_X_SILVER_COLOR_IDS.has(normalizedColorId)) return 'silver';
    if (MU_X_WHITE_COLOR_IDS.has(normalizedColorId)) return 'white';
    return 'cool';
  }

  if (OPAQUE_COLOR_IDS.has(normalizedColorId)) return 'opaque';
  if (LIGHT_DARK_COLOR_IDS.has(normalizedColorId)) return 'light-dark';

  return 'light';
};
